import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Gavel, Plus, Send, Clock, CheckCircle2, XCircle, AlertCircle } from "lucide-react";

const TIERS = [
  { value: "discepolo", label: "Discepolo (€490/anno)" },
  { value: "custode", label: "Custode (€1.490/anno)" },
  { value: "ambasciatore", label: "Ambasciatore (€4.900/anno)" },
];
const FORMATS = ["0.75L", "1.5L Magnum", "3L Jeroboam", "5L Jeroboam Bordeaux", "6L Methuselah", "9L Salmanazar", "12L Balthazar"];

const eur = (n) => `€${Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;

const STATUS_META = {
  pending: { label: "In attesa", icon: Clock, cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  approved: { label: "Approvata · Asta pubblicata", icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  rejected: { label: "Rifiutata", icon: XCircle, cls: "bg-red-500/15 text-red-500 border-red-500/30" },
};

function ProposalForm({ onSent, onCancel }) {
  const [form, setForm] = useState({
    lot_name: "", producer: "", description: "", format: "0.75L", bottles: 1,
    vintage: new Date().getFullYear() - 5, region: "", image: "",
    starting_bid: 1000, estimate_min: 1500, estimate_max: 3000, provenance: "",
    tier_required: "discepolo", suggested_duration_days: 14, proposer_notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (Number(form.estimate_max) < Number(form.estimate_min)) { toast.error("Stima max ≥ stima min"); return; }
    if (Number(form.estimate_min) < Number(form.starting_bid)) { toast.error("Stima min ≥ base d'asta"); return; }
    setBusy(true);
    try {
      await api.post("/auctions/propose", {
        ...form,
        bottles: Number(form.bottles),
        vintage: form.vintage ? Number(form.vintage) : null,
        starting_bid: Number(form.starting_bid),
        estimate_min: Number(form.estimate_min),
        estimate_max: Number(form.estimate_max),
        suggested_duration_days: Number(form.suggested_duration_days),
      });
      toast.success("Proposta inviata · attendi approvazione admin");
      onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  return (
    <form data-testid="propose-form" onSubmit={submit} className="bg-card border border-border rounded-sm p-6 space-y-4">
      <div className="text-sm text-muted-foreground border-l-2 border-[#C9A961] pl-3 italic">
        La tua proposta verrà valutata dallo staff Amazonico 3000. Se approvata, l'asta sarà pubblicata sul Club Privilegia.
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Nome Lotto *</label>
          <Input data-testid="prop-lot" required value={form.lot_name} onChange={(e) => setForm({ ...form, lot_name: e.target.value })} placeholder="Es. Sassicaia 2015 · Lotto verticale 6 bottiglie" className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Produttore *</label>
          <Input data-testid="prop-producer" required value={form.producer} onChange={(e) => setForm({ ...form, producer: e.target.value })} placeholder="Tenuta San Guido" className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold">Descrizione del lotto *</label>
          <Textarea data-testid="prop-description" required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Storia, conservazione, perché è un lotto unico..." className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Formato</label>
          <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Bottiglie totali</label>
          <Input type="number" min="1" value={form.bottles} onChange={(e) => setForm({ ...form, bottles: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Annata</label>
          <Input type="number" min="1900" max={new Date().getFullYear()} value={form.vintage || ""} onChange={(e) => setForm({ ...form, vintage: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Regione</label>
          <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Toscana" className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold">URL Immagine</label>
          <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://..." className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Base d'asta proposta (€) *</label>
          <Input data-testid="prop-starting" required type="number" min="1" value={form.starting_bid} onChange={(e) => setForm({ ...form, starting_bid: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Tier consigliato</label>
          <Select value={form.tier_required} onValueChange={(v) => setForm({ ...form, tier_required: v })}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Stima min (€) *</label>
          <Input required type="number" min="1" value={form.estimate_min} onChange={(e) => setForm({ ...form, estimate_min: e.target.value })} className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Stima max (€) *</label>
          <Input required type="number" min="1" value={form.estimate_max} onChange={(e) => setForm({ ...form, estimate_max: e.target.value })} className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold">Provenance</label>
          <Input value={form.provenance} onChange={(e) => setForm({ ...form, provenance: e.target.value })} placeholder="Direttamente dalla cantina · Cassa originale · Etichette in perfette condizioni" className="mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold">Durata suggerita (giorni)</label>
          <Input type="number" min="1" max="60" value={form.suggested_duration_days} onChange={(e) => setForm({ ...form, suggested_duration_days: e.target.value })} className="mt-1" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold">Note interne (visibili all'admin)</label>
          <Textarea rows={2} value={form.proposer_notes} onChange={(e) => setForm({ ...form, proposer_notes: e.target.value })} placeholder="Es. Richieste di un cliente top, ho già 3 acquirenti seri interessati..." className="mt-1" />
        </div>
      </div>

      <div className="flex gap-2 pt-3 border-t border-border">
        <Button data-testid="prop-submit" type="submit" disabled={busy} className="bg-[hsl(var(--primary))] gap-2"><Send className="h-4 w-4" />Invia proposta</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Annulla</Button>
      </div>
    </form>
  );
}

function ProposalCard({ p }) {
  const meta = STATUS_META[p.status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <div data-testid={`my-prop-${p.id}`} className="bg-card border border-border rounded-sm p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="editorial-heading text-xl">{p.lot_name}</h3>
          <div className="text-sm text-muted-foreground mt-0.5">{p.producer} · {p.format} · {p.bottles} btl{p.vintage ? ` · ${p.vintage}` : ""}</div>
        </div>
        <span className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 border rounded-sm font-bold ${meta.cls}`}><Icon className="h-3 w-3" />{meta.label}</span>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3 text-xs border-t border-border pt-3">
        <div><span className="text-muted-foreground">Base</span><div className="font-bold text-[#C9A961]">{eur(p.starting_bid)}</div></div>
        <div><span className="text-muted-foreground">Stima</span><div>{eur(p.estimate_min)}-{eur(p.estimate_max)}</div></div>
        <div><span className="text-muted-foreground">Durata</span><div>{p.suggested_duration_days}g</div></div>
      </div>

      {p.admin_notes && (
        <div className="mt-3 p-3 bg-muted text-xs italic rounded-sm border-l-2 border-[#C9A961]">
          <strong className="text-[#C9A961] not-italic uppercase tracking-wider text-[10px]">Note admin: </strong>{p.admin_notes}
        </div>
      )}
      {p.status === "approved" && p.approved_auction_id && (
        <a href={`/club/auctions/${p.approved_auction_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-xs text-[#C9A961] underline">
          Vedi asta pubblicata →
        </a>
      )}
    </div>
  );
}

export default function B2BProposeAuction() {
  const [proposals, setProposals] = useState([]);
  const [creating, setCreating] = useState(false);

  const reload = () => api.get("/auctions/proposals/mine").then((r) => setProposals(r.data.proposals || []));
  useEffect(() => { reload(); }, []);

  return (
    <div data-testid="b2b-propose-page" className="container-xl py-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--accent))] flex items-center gap-2 mb-2"><Gavel className="h-3 w-3" />B2B · Aste private</div>
          <h1 className="editorial-heading text-4xl md:text-5xl text-[hsl(var(--foreground))]">Proponi un lotto in asta</h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">Sottoponi un lotto al Club Privilegia. Lo staff Amazonico 3000 lo valuterà e, se approvato, sarà pubblicato come asta privata.</p>
        </div>
        {!creating && (
          <Button data-testid="prop-new-btn" onClick={() => setCreating(true)} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 gap-2">
            <Plus className="h-4 w-4" />Nuova proposta
          </Button>
        )}
      </div>

      {creating && (
        <div className="mt-6">
          <ProposalForm onSent={() => { setCreating(false); reload(); }} onCancel={() => setCreating(false)} />
        </div>
      )}

      <div className="mt-8 space-y-4">
        {proposals.length === 0 && !creating ? (
          <div className="bg-card border border-border rounded-sm p-12 text-center">
            <Gavel className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <div className="text-lg font-semibold">Nessuna proposta ancora</div>
            <div className="text-sm text-muted-foreground mt-1">Hai un lotto eccezionale da mettere all'asta? Inizia da qui.</div>
          </div>
        ) : (
          proposals.map((p) => <ProposalCard key={p.id} p={p} />)
        )}
      </div>
    </div>
  );
}
